import styled, { css } from 'styled-components';

import { Text } from '../../../../components';
import type { ListPlacement } from '../../game-list-geometry';
import { matchScrollMargin } from '../GameListFilter/GameListFilter.styles';
import { ANIM, COLORS, GAME_LIST_FILTER, MOBILE, REDUCED_MOTION, SPACE } from '@/nova/theme/tokens';

/* Positioned: a game sent off screen from its menu leaves as a copy placed in here (see send-away). On the page the
   filter's row sits as far above the games as the Games tab's other parts sit from each other (GameLists' Body); the
   geometry's ROW_LIST_GAP_PX is this gap, so keep the two equal. */
export const Wrap = styled.div<{ $placement: ListPlacement }>`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: ${({ $placement }) => ($placement === 'dialog' ? SPACE[20] : SPACE[24])};
`;

/* The list laid out like the site's game rows, but WRAPPING onto new rows instead of scrolling sideways,
   so the whole order reads left-to-right, top-to-bottom. */
export const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(156px, 1fr));
  gap: 18px 14px;

  ${MOBILE} {
    grid-template-columns: repeat(auto-fill, minmax(132px, 1fr));
    gap: 14px 10px;
  }
`;

/* One game in the order: the draggable slot. The grabbed tile becomes a dimmed placeholder (the drag element
   carries the visual) while the others glide out of its way. */
export const Slot = styled.div<{
  $dragging?: boolean;
  $draggable?: boolean;
  $dimmed?: boolean;
  $current?: boolean;
  $placement: ListPlacement;
}>`
  position: relative;
  min-width: 0;
  cursor: ${({ $draggable }) => ($draggable ? 'grab' : 'default')};
  will-change: transform;
  /* On a touch screen a tile is lifted by resting a finger on it: no text selection and no iOS callout under that
     finger, while a swipe that starts on a tile still scrolls until the hold completes. */
  user-select: none;
  -webkit-user-select: none;
  -webkit-touch-callout: none;
  touch-action: manipulation;
  transition: opacity ${ANIM.INTERACT_MS}ms ${ANIM.EASE};
  ${({ $placement }) => matchScrollMargin($placement)}
  /* Moving a game must not move the page: without this the browser keeps a moved tile that was the scroll
     anchor where it was on screen, scrolling the page after it. */
  overflow-anchor: none;

  &:active {
    cursor: ${({ $draggable }) => ($draggable ? 'grabbing' : 'default')};
  }

  /* Filtered out: faded back, still in its place in the order. */
  ${({ $dimmed }) =>
    $dimmed &&
    css`
      opacity: ${GAME_LIST_FILTER.DIMMED_OPACITY};
    `}

  ${({ $dragging }) =>
    $dragging &&
    css`
      opacity: 0.35;
    `}

  /* The match the filter's steps are on. */
  ${({ $current }) =>
    $current &&
    css`
      border-radius: 16px;
      outline: 2px solid ${COLORS.ctaBorder};
      outline-offset: 4px;
    `}

  /* A tile in use (its menu open) sits above its neighbours, which the menu may overlap, and at full
     strength even when filtered out. */
  &:focus-within {
    z-index: 3;
    opacity: 1;
  }

  ${REDUCED_MOTION} {
    transition: none;
  }
`;

/* The square artwork, as the site shows it. */
export const Art = styled.div`
  position: relative;
  aspect-ratio: 1 / 1;
  border-radius: 14px;
  overflow: hidden;
  background: ${COLORS.surfaceInset};
  border: 1px solid ${COLORS.hairline};
`;

export const Img = styled.img`
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
  -webkit-user-drag: none; /* so grabbing the image drags the TILE, not the image file */
  user-select: none;
`;

/* The game's name, under the artwork. Always two lines tall (clamped to two, and a one-line name keeps the
   empty second line) so the ID + provider line under it sits at the same height on every tile of a row. */
export const Name = styled(Text).attrs({ tag: 'span' as const })<{ $muted?: boolean }>`
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  height: 2.5em; /* two lines of line-height 1.25 */
  margin: ${SPACE[8]} ${SPACE[2]} 0;
  font-size: 13px;
  font-weight: 700;
  line-height: 1.25;
  color: ${({ $muted }) => ($muted ? COLORS.textMuted : COLORS.heading)};
  overflow-wrap: anywhere;
`;

/* A game the catalogue no longer holds: in place of its art, what happened to it. */
export const Missing = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: ${SPACE[8]};
  padding: ${SPACE[12]};
  text-align: center;
  color: ${COLORS.textMuted};
`;

export const MissingText = styled(Text).attrs({ tag: 'span' as const })`
  font-size: 12px;
  line-height: 1.35;
`;

/* The position number (1-based), the order the site will render. Same solid pill as the banner carousels. */
export const OrderBadge = styled.span`
  position: absolute;
  top: 8px;
  left: 8px;
  z-index: 2;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 24px;
  height: 24px;
  padding: 0 7px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 800;
  font-variant-numeric: tabular-nums;
  color: ${COLORS.heading};
  background: ${COLORS.surfaceCard};
  border: 1px solid ${COLORS.hairline};
`;

/* ID + provider, under the name. */
export const Meta = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  min-height: 22px;
  padding: ${SPACE[4]} ${SPACE[2]} 0;
`;

/* Drag affordance at the head of the tile's meta line, like the curated banners' handle: a quiet grip
   that says the tile can be dragged into a new order. */
export const DragHandle = styled.span`
  flex: none;
  display: inline-flex;
  color: ${COLORS.textFaint};
  /* A finger on it drags the tile at once: the browser must not take that touch for a scroll. A larger target for
     the finger than the 16px icon, with the line's layout unchanged. */
  touch-action: none;
  padding: 8px;
  margin: -8px;
`;

/* The tile's move menu, at the end of the ID + provider line; its trigger overhangs the line's padding so
   the tile keeps its height. */
export const TileMenu = styled.div`
  flex: none;
  display: inline-flex;
  margin: -6px -8px -6px 0;
`;

export const MetaText = styled(Text).attrs({ tag: 'span' as const })`
  flex: 1 1 auto;
  min-width: 0;
  font-size: 12px;
  line-height: 1.35;
  color: ${COLORS.textMuted};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

/* Custom drag element: a solid, raised copy of the tile that follows the pointer (portalled to <body>). */
export const DragLayer = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  /* Above every layer a list can sit in (a Sheet is 2000, toasts 2100, confirm dialogs 2200): the tile in the
     hand must never pass under the dialog it is being dragged in. It only exists during a drag. */
  z-index: 3000;
  pointer-events: none;
  will-change: transform;
`;

export const DragGhost = styled.div`
  border-radius: 14px;
  overflow: hidden;
  box-shadow: ${COLORS.menuShadow};
  transform: scale(1.04);
  transform-origin: center;
`;

export const Footer = styled.div`
  display: flex;
  justify-content: center;
`;

/* "Show more": the same orange outline pill as the Creatives secondary actions. */
export const MoreBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 9px 18px;
  border-radius: 999px;
  border: 1px solid ${COLORS.ctaBorder};
  background: transparent;
  color: ${COLORS.ctaBorder};
  font-family: inherit;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  transition: background ${ANIM.CREATIVE_HOVER_MS}ms ${ANIM.EASE};

  &:hover {
    background: ${COLORS.ctaTint};
  }
`;
